import React from 'react';
import { Button, Group, Modal, Stack, Text, Textarea } from '@cowbell/ui';

/**
 * Above the MUI dialogs (1300) the details page opens, below the notification toasts (1400). Mantine's own default
 * of 200 would open this behind the page header.
 */
export const SYSTEM_STATUS_MODAL_Z = 1350;

const noop = () => undefined;

export const isReasonProvided = (reason: string): boolean =>
  reason.trim().length > 0;

interface SystemStatusReasonModalProps {
  readonly statusLabel: string;
  readonly isSubmitting: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}

/** Asks why a person is setting a status the system normally sets. The reason goes in the submission notes. */
export const SystemStatusReasonModal = ({
  statusLabel,
  isSubmitting,
  onConfirm,
  onClose,
}: SystemStatusReasonModalProps) => {
  const [reason, setReason] = React.useState('');
  const canSubmit = isReasonProvided(reason) && !isSubmitting;

  return (
    <Modal
      opened
      onClose={isSubmitting ? noop : onClose}
      closeOnEscape={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      withCloseButton={!isSubmitting}
      title={`Set status to ${statusLabel}`}
      centered
      zIndex={SYSTEM_STATUS_MODAL_Z}
    >
      <Stack gap="md">
        <Text size="sm">
          The system normally sets this status. Say why you are setting it
          yourself. Your reason is saved in the submission notes.
        </Text>
        <Textarea
          label="Reason"
          required
          autosize
          minRows={3}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          data-autofocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            Update status
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
